/**
 * Renderer-side helpers for the "quick capture" global-hotkey awareness tip.
 * The hotkey itself is OS-level (CAPTURE_HOTKEY / tray label in
 * public/electron.js), not one of the in-app remappable shortcuts in
 * keyboardShortcuts.js — keep this label in sync with that string.
 */

export const CAPTURE_HOTKEY_LABEL = "Ctrl+Option+K";

const LEGACY_SEEN_KEY = "starmesh.captureHotkeyTip.seen";
const SEEN_VERSION_KEY = "starmesh.captureHotkeyTip.seenVersion";

/**
 * Migrate the old boolean "seen" flag to a version stamp so existing users
 * are not re-prompted until the next app upgrade.
 *
 * @param {string} currentVersion
 * @example
 * migrateLegacyCaptureHotkeyTipSeen("0.1.7");
 */
const migrateLegacyCaptureHotkeyTipSeen = (currentVersion) => {
  try {
    if (
      localStorage.getItem(LEGACY_SEEN_KEY) === "1" &&
      !localStorage.getItem(SEEN_VERSION_KEY)
    ) {
      localStorage.setItem(SEEN_VERSION_KEY, currentVersion);
      localStorage.removeItem(LEGACY_SEEN_KEY);
    }
  } catch (_) {
    // Private mode / blocked storage — skip migration.
  }
};

/**
 * True when the quick-capture awareness tip should show for this app version.
 * Shown on first install (no stored version) and again after each upgrade.
 *
 * @param {string} currentVersion Running desktop app version from Electron.
 * @returns {boolean}
 * @example
 * shouldShowCaptureHotkeyTip("0.1.7"); // true on first launch
 * markCaptureHotkeyTipSeen("0.1.7");
 * shouldShowCaptureHotkeyTip("0.1.7"); // false
 * shouldShowCaptureHotkeyTip("0.1.8"); // true after upgrade
 */
export const shouldShowCaptureHotkeyTip = (currentVersion) => {
  const version = String(currentVersion || "").trim();
  if (!version) return true;
  migrateLegacyCaptureHotkeyTipSeen(version);
  try {
    const seenVersion = localStorage.getItem(SEEN_VERSION_KEY);
    if (!seenVersion) return true;
    return seenVersion !== version;
  } catch (_) {
    return true;
  }
};

/**
 * Persist that the user dismissed the tip for this app version.
 *
 * @param {string} currentVersion Running desktop app version from Electron.
 * @example
 * markCaptureHotkeyTipSeen("0.1.7");
 */
export const markCaptureHotkeyTipSeen = (currentVersion) => {
  const version = String(currentVersion || "").trim();
  if (!version) return;
  try {
    localStorage.setItem(SEEN_VERSION_KEY, version);
    localStorage.removeItem(LEGACY_SEEN_KEY);
  } catch (_) {
    // Private mode / blocked storage — tip may reappear next session.
  }
};

/**
 * @deprecated Use shouldShowCaptureHotkeyTip(currentVersion) instead.
 */
export const hasSeenCaptureHotkeyTip = () => {
  try {
    return (
      Boolean(localStorage.getItem(SEEN_VERSION_KEY)) ||
      localStorage.getItem(LEGACY_SEEN_KEY) === "1"
    );
  } catch (_) {
    return false;
  }
};

/**
 * Whether the desktop capture spotlight should open, given the main-process
 * opt-in state (null when the bridge is unavailable — then version gating
 * alone decides).
 *
 * An install that turned capture down is never asked again: that was an
 * answer, and repeating it every upgrade is nagging. An undecided install
 * (fresh install, capture off) is always asked, version stamp or not.
 *
 * @example
 * shouldShowCaptureSpotlight({ captureState: { decided: false, enabled: false } });
 * // true — fresh install, needs the opt-in prompt
 *
 * @example
 * shouldShowCaptureSpotlight({ captureState: { decided: true, enabled: false } });
 * // false — the user said no
 */
export const shouldShowCaptureSpotlight = ({ captureState = null, version = "" } = {}) => {
  if (captureState && captureState.decided && !captureState.enabled) return false;
  if (captureState && !captureState.decided) return true;
  return shouldShowCaptureHotkeyTip(version);
};
